/*
 * The editor's agent-facing API.
 *
 * Every tool here is a thin wrapper: schema in, EditorCommands method out. No
 * editor logic lives in this file — that is what keeps the commands testable on
 * their own and keeps the WebMCP layer swappable.
 *
 * Design rules applied throughout (see docs/webmcp.md §4):
 *   - explicit units and coordinate spaces, never a bare {x, y}
 *   - additionalProperties:false everywhere, so an agent's typo is a clear error
 *   - read tools are marked readOnly and never mutate
 *   - every mutation returns the changed object plus documentRevision
 */

/** Layer reference, shared by every tool that targets one layer. */
const LAYER_ID = {
  type: 'integer',
  description: 'Id of the layer, as returned by inspect_layers. Omit to use the currently active layer.'
}

/**
 * Optimistic concurrency. An agent that read the document at revision N can pass
 * it back; if a human edited in between, the write is refused instead of silently
 * overwriting their work.
 */
const EXPECTED_REVISION = {
  type: 'integer',
  minimum: 0,
  description: 'Optional. The documentRevision the caller last observed. The call is rejected with REVISION_MISMATCH if the document has changed since.'
}

const COORDINATE_SPACE = {
  type: 'string',
  enum: ['canvas'],
  default: 'canvas',
  description: 'Coordinate space for x and y. Only "canvas" is supported: pixels from the top-left corner of the canvas.'
}

function objectSchema (properties, required = []) {
  return { type: 'object', properties, required, additionalProperties: false }
}

/**
 * @param {import('../../editor/editorCommands.js').EditorCommands} commands
 * @returns {Array<object>} tool definitions for the ToolRegistry
 */
export function createEditorTools (commands) {
  return [
    // ------------------------------------------------------------- READ tools
    {
      name: 'inspect_document',
      title: 'Inspect document',
      description:
        'Returns the current state of the image document: canvas size in pixels, how many layers there are, ' +
        'which layer is active, and how many undo/redo steps are available. Read-only. Call this first to ' +
        'find out what you are working with, and to get the documentRevision.',
      readOnly: true,
      inputSchema: objectSchema({}),
      handler: () => commands.inspectDocument()
    },
    {
      name: 'inspect_layers',
      title: 'Inspect layers',
      description:
        'Lists every layer in the document, bottom to top, with its id, name, type, position, size, opacity, ' +
        'visibility, rotation and applied adjustments. Text layers also include their text content. Read-only.',
      readOnly: true,
      inputSchema: objectSchema({}),
      handler: () => commands.inspectLayers()
    },
    {
      name: 'inspect_selection',
      title: 'Inspect selection',
      description:
        'Returns the current selection rectangle in canvas pixels, or null when nothing is selected. Read-only.',
      readOnly: true,
      inputSchema: objectSchema({}),
      handler: () => commands.inspectSelection()
    },

    // ------------------------------------------------------------ LAYER tools
    {
      name: 'select_layer',
      title: 'Select layer',
      description:
        'Makes a layer the active one. Tools that take an optional layerId act on the active layer when it is omitted.',
      inputSchema: objectSchema({ layerId: { ...LAYER_ID, description: 'Id of the layer to activate.' }, expectedRevision: EXPECTED_REVISION }, ['layerId']),
      handler: (args) => commands.selectLayer(args)
    },
    {
      name: 'add_text_layer',
      title: 'Add text layer',
      description:
        'Creates a new text layer at a position on the canvas. The position is the top-left corner of the text ' +
        'in canvas pixels. Returns the created layer, including its generated id.',
      inputSchema: objectSchema({
        text: { type: 'string', minLength: 1, maxLength: 2000, description: 'The text to display. Use \\n for line breaks.' },
        x: { type: 'integer', default: 0, description: 'Left edge of the text, in pixels from the canvas left.' },
        y: { type: 'integer', default: 0, description: 'Top edge of the text, in pixels from the canvas top.' },
        coordinateSpace: COORDINATE_SPACE,
        fontSize: { type: 'integer', minimum: 1, maximum: 500, default: 40, description: 'Font size in pixels.' },
        fontFamily: { type: 'string', default: 'Arial', description: 'Font family name, e.g. "Arial", "Verdana", "Impact".' },
        color: { type: 'string', pattern: '^#[0-9a-fA-F]{6}$', default: '#000000', description: 'Fill colour as a #rrggbb hex string.' },
        bold: { type: 'boolean', default: false },
        italic: { type: 'boolean', default: false },
        name: { type: 'string', maxLength: 100, description: 'Optional layer name. Defaults to a name derived from the text.' },
        expectedRevision: EXPECTED_REVISION
      }, ['text']),
      handler: (args) => commands.addTextLayer(args)
    },
    {
      name: 'move_layer',
      title: 'Move layer',
      description:
        'Moves a layer. With mode "absolute" (the default) x and y become the layer\'s new top-left corner in ' +
        'canvas pixels. With mode "relative" they are added to the current position, so {x: 50, y: 0, mode: ' +
        '"relative"} moves the layer 50 pixels to the right from wherever it is now.',
      inputSchema: objectSchema({
        layerId: LAYER_ID,
        x: { type: 'integer', description: 'Target x, or x offset when mode is "relative". Pixels.' },
        y: { type: 'integer', description: 'Target y, or y offset when mode is "relative". Pixels.' },
        coordinateSpace: COORDINATE_SPACE,
        mode: { type: 'string', enum: ['absolute', 'relative'], default: 'absolute' },
        expectedRevision: EXPECTED_REVISION
      }, ['x', 'y']),
      handler: (args) => commands.moveLayer(args)
    },
    {
      name: 'resize_layer',
      title: 'Resize layer',
      description:
        'Sets a layer\'s width and/or height in pixels. This scales the layer only — the canvas is unchanged. ' +
        'Use resize_canvas to change the document size.',
      inputSchema: objectSchema({
        layerId: LAYER_ID,
        width: { type: 'integer', minimum: 1, maximum: 8192, description: 'New layer width in pixels.' },
        height: { type: 'integer', minimum: 1, maximum: 8192, description: 'New layer height in pixels.' },
        expectedRevision: EXPECTED_REVISION
      }),
      handler: (args) => commands.resizeLayer(args)
    },
    {
      name: 'delete_layer',
      title: 'Delete layer',
      description:
        'Deletes a layer. Refuses to delete the last remaining layer. Returns the deleted layer so the change is auditable.',
      inputSchema: objectSchema({ layerId: { ...LAYER_ID, description: 'Id of the layer to delete.' }, expectedRevision: EXPECTED_REVISION }, ['layerId']),
      handler: (args) => commands.deleteLayer(args)
    },
    {
      name: 'set_layer_opacity',
      title: 'Set layer opacity',
      description: 'Sets a layer\'s opacity, where 0 is fully transparent and 1 is fully opaque.',
      inputSchema: objectSchema({
        layerId: LAYER_ID,
        opacity: { type: 'number', minimum: 0, maximum: 1, description: 'Opacity from 0 (transparent) to 1 (opaque).' },
        expectedRevision: EXPECTED_REVISION
      }, ['opacity']),
      handler: (args) => commands.setLayerOpacity(args)
    },
    {
      name: 'duplicate_layer',
      title: 'Duplicate layer',
      description:
        'Creates a copy of a layer directly above it, at the same position, named "<original> copy". ' +
        'The copy becomes the active layer.',
      inputSchema: objectSchema({ layerId: LAYER_ID, expectedRevision: EXPECTED_REVISION }),
      handler: (args) => commands.duplicateLayer(args)
    },

    // --------------------------------------------------------- DOCUMENT tools
    {
      name: 'resize_canvas',
      title: 'Resize canvas',
      description:
        'Changes the document canvas size in pixels. By default layers keep their pixel position and size, so ' +
        'the canvas is cropped or extended around them. Set scaleLayers to true to scale layer positions and ' +
        'sizes with the canvas instead — that is what you usually want when converting to another aspect ratio.',
      inputSchema: objectSchema({
        width: { type: 'integer', minimum: 1, maximum: 8192, description: 'New canvas width in pixels.' },
        height: { type: 'integer', minimum: 1, maximum: 8192, description: 'New canvas height in pixels.' },
        scaleLayers: { type: 'boolean', default: false, description: 'Scale layer positions and sizes to the new canvas.' },
        expectedRevision: EXPECTED_REVISION
      }, ['width', 'height']),
      handler: (args) => commands.resizeCanvas(args)
    },
    {
      name: 'crop_image',
      title: 'Crop image',
      description:
        'Crops the document to a rectangle given in canvas pixels. The rectangle must lie inside the current ' +
        'canvas. Layer pixels are preserved and simply re-anchored, so the crop is exactly reversible with undo.',
      inputSchema: objectSchema({
        x: { type: 'integer', minimum: 0, description: 'Left edge of the crop rectangle, in canvas pixels.' },
        y: { type: 'integer', minimum: 0, description: 'Top edge of the crop rectangle, in canvas pixels.' },
        width: { type: 'integer', minimum: 1, maximum: 8192, description: 'Crop width in pixels.' },
        height: { type: 'integer', minimum: 1, maximum: 8192, description: 'Crop height in pixels.' },
        coordinateSpace: COORDINATE_SPACE,
        expectedRevision: EXPECTED_REVISION
      }, ['x', 'y', 'width', 'height']),
      handler: (args) => commands.cropImage(args)
    },
    {
      name: 'rotate_image',
      title: 'Rotate layer',
      description:
        'Sets the rotation of a layer to an absolute angle in degrees clockwise (0-359). Note this rotates the ' +
        'layer, not the canvas; use resize_canvas if the document shape must change too.',
      inputSchema: objectSchema({
        layerId: LAYER_ID,
        degrees: { type: 'integer', description: 'Absolute rotation in degrees clockwise. Values outside 0-359 are normalised.' },
        expectedRevision: EXPECTED_REVISION
      }, ['degrees']),
      handler: (args) => commands.rotateImage(args)
    },

    // ------------------------------------------------------- ADJUSTMENT tools
    {
      name: 'adjust_brightness',
      title: 'Adjust brightness',
      description:
        'Applies a brightness adjustment to a layer. Value is a percentage from -100 (much darker) through 0 ' +
        '(unchanged) to 100 (much brighter). Adjustments stack; call undo to remove the last one.',
      inputSchema: objectSchema({
        layerId: LAYER_ID,
        value: { type: 'integer', minimum: -100, maximum: 100, description: 'Brightness change in percent, -100 to 100.' },
        expectedRevision: EXPECTED_REVISION
      }, ['value']),
      handler: (args) => commands.adjust({ ...args, filter: 'brightness' })
    },
    {
      name: 'adjust_contrast',
      title: 'Adjust contrast',
      description:
        'Applies a contrast adjustment to a layer. Value is a percentage from -100 (flat) through 0 (unchanged) to 100 (high contrast).',
      inputSchema: objectSchema({
        layerId: LAYER_ID,
        value: { type: 'integer', minimum: -100, maximum: 100, description: 'Contrast change in percent, -100 to 100.' },
        expectedRevision: EXPECTED_REVISION
      }, ['value']),
      handler: (args) => commands.adjust({ ...args, filter: 'contrast' })
    },
    {
      name: 'adjust_saturation',
      title: 'Adjust saturation',
      description:
        'Applies a saturation adjustment to a layer. Value is a percentage from -100 (greyscale) through 0 (unchanged) to 100 (vivid).',
      inputSchema: objectSchema({
        layerId: LAYER_ID,
        value: { type: 'integer', minimum: -100, maximum: 100, description: 'Saturation change in percent, -100 to 100.' },
        expectedRevision: EXPECTED_REVISION
      }, ['value']),
      handler: (args) => commands.adjust({ ...args, filter: 'saturate' })
    },

    // ------------------------------------------------------------ SHAPE tools
    {
      name: 'set_background_color',
      title: 'Set background colour',
      description:
        'Fills the whole canvas with a solid colour on a new layer placed underneath everything else. ' +
        'Use this first when building a graphic from scratch, because a new document is empty and text ' +
        'placed on it would sit on nothing. Returns the created layer.',
      inputSchema: objectSchema({
        color: { type: 'string', pattern: '^#[0-9a-fA-F]{6}$', description: 'Background colour as a #rrggbb hex string.' },
        expectedRevision: EXPECTED_REVISION
      }, ['color']),
      handler: (args) => commands.setBackgroundColor(args)
    },
    {
      name: 'add_rectangle',
      title: 'Add rectangle',
      description:
        'Draws a filled rectangle on its own layer, in canvas pixels. Use it for colour blocks, banners, ' +
        'buttons, badges, underlines and panels behind text. The rectangle is a normal layer, so it can be ' +
        'moved, reordered, faded and undone like any other.',
      inputSchema: objectSchema({
        x: { type: 'integer', description: 'Left edge in pixels from the canvas left.' },
        y: { type: 'integer', description: 'Top edge in pixels from the canvas top.' },
        width: { type: 'integer', minimum: 1, maximum: 8192, description: 'Width in pixels.' },
        height: { type: 'integer', minimum: 1, maximum: 8192, description: 'Height in pixels.' },
        color: { type: 'string', pattern: '^#[0-9a-fA-F]{6}$', description: 'Fill colour as a #rrggbb hex string.' },
        cornerRadius: { type: 'integer', minimum: 0, maximum: 512, default: 0, description: 'Corner radius in pixels. 0 is a sharp rectangle.' },
        opacity: { type: 'number', minimum: 0, maximum: 1, default: 1, description: 'Opacity from 0 (transparent) to 1 (opaque).' },
        name: { type: 'string', maxLength: 100, description: 'Optional layer name.' },
        sendToBack: { type: 'boolean', default: false, description: 'Place the rectangle underneath every existing layer.' },
        coordinateSpace: COORDINATE_SPACE,
        expectedRevision: EXPECTED_REVISION
      }, ['x', 'y', 'width', 'height', 'color']),
      handler: (args) => commands.addRectangle(args)
    },

    // ------------------------------------------------------ ARRANGEMENT tools
    {
      name: 'reorder_layer',
      title: 'Reorder layer',
      description:
        'Moves a layer through the stacking order. Layers later in the order are drawn on top. Use "front" ' +
        'to put a headline above a colour block, or "back" to push a background behind everything. This is ' +
        'one undo step even when the layer moves several places.',
      inputSchema: objectSchema({
        layerId: LAYER_ID,
        position: {
          type: 'string',
          enum: ['front', 'back', 'forward', 'backward'],
          description: 'front and back jump to the top or bottom of the stack; forward and backward move one place.'
        },
        expectedRevision: EXPECTED_REVISION
      }, ['position']),
      handler: (args) => commands.reorderLayer(args)
    },
    {
      name: 'align_layer',
      title: 'Align layer',
      description:
        'Aligns a layer to the canvas without you having to compute pixel positions. Give horizontal, ' +
        'vertical, or both. Centring a headline is align_layer with horizontal "center".',
      inputSchema: objectSchema({
        layerId: LAYER_ID,
        horizontal: { type: 'string', enum: ['left', 'center', 'right'], description: 'Horizontal alignment against the canvas.' },
        vertical: { type: 'string', enum: ['top', 'middle', 'bottom'], description: 'Vertical alignment against the canvas.' },
        expectedRevision: EXPECTED_REVISION
      }),
      handler: (args) => commands.alignLayer(args)
    },
    {
      name: 'set_text',
      title: 'Edit text layer',
      description:
        'Changes the content or styling of an existing text layer, without deleting and recreating it. ' +
        'Anything you leave out keeps its current value, so you can restyle without retyping. ' +
        'Fails with UNSUPPORTED_LAYER_TYPE on a layer that is not text.',
      inputSchema: objectSchema({
        layerId: LAYER_ID,
        text: { type: 'string', minLength: 1, maxLength: 2000, description: 'New text content. Use \\n for line breaks.' },
        fontSize: { type: 'integer', minimum: 1, maximum: 500, description: 'Font size in pixels.' },
        fontFamily: { type: 'string', description: 'Font family name.' },
        color: { type: 'string', pattern: '^#[0-9a-fA-F]{6}$', description: 'Fill colour as a #rrggbb hex string.' },
        bold: { type: 'boolean' },
        italic: { type: 'boolean' },
        expectedRevision: EXPECTED_REVISION
      }),
      handler: (args) => commands.setText(args)
    },
    {
      name: 'set_layer_visibility',
      title: 'Show or hide layer',
      description:
        'Shows or hides a layer without deleting it. Hidden layers keep their position and are excluded ' +
        'from the export.',
      inputSchema: objectSchema({
        layerId: LAYER_ID,
        visible: { type: 'boolean', description: 'true shows the layer, false hides it.' },
        expectedRevision: EXPECTED_REVISION
      }, ['visible']),
      handler: (args) => commands.setLayerVisibility(args)
    },

    // ------------------------------------------------------- HIGH-LEVEL tools
    {
      name: 'create_social_variant',
      title: 'Create social media variant',
      description:
        'Reshapes the current document into a named social media format, carrying the existing layout across ' +
        'proportionally. Use this when someone asks for "a square version for Instagram", "a portrait version", ' +
        'a story, or a YouTube thumbnail. Returns the new canvas size and the repositioned layers. ' +
        'This is one undo step, so the person can revert it with a single undo.',
      inputSchema: objectSchema({
        preset: {
          type: 'string',
          enum: [
            'instagram_square', 'instagram_portrait', 'instagram_story',
            'facebook_post', 'linkedin_post', 'x_post', 'youtube_thumbnail'
          ],
          description: 'Target format. instagram_square is 1080x1080, instagram_portrait 1080x1350, instagram_story 1080x1920, facebook_post 1200x630, linkedin_post 1200x627, x_post 1600x900, youtube_thumbnail 1280x720.'
        },
        scaleLayers: {
          type: 'boolean',
          default: true,
          description: 'Scale the existing layout into the new canvas. Set false to keep layers at their pixel positions and crop or extend around them instead.'
        },
        expectedRevision: EXPECTED_REVISION
      }, ['preset']),
      handler: (args) => commands.createSocialVariant(args)
    },

    // ---------------------------------------------------------- HISTORY tools
    {
      name: 'undo',
      title: 'Undo',
      description:
        'Undoes the most recent change to the document — whether it was made by you or by the person using the ' +
        'editor, since both share one history. Returns which action was undone.',
      inputSchema: objectSchema({}),
      handler: () => commands.undo()
    },
    {
      name: 'redo',
      title: 'Redo',
      description: 'Redoes the most recently undone change. Returns which action was redone.',
      inputSchema: objectSchema({}),
      handler: () => commands.redo()
    },

    // ----------------------------------------------------------- EXPORT tools
    {
      name: 'export_image',
      title: 'Export image',
      description:
        'Renders the whole document to an image and returns it as a data URL, together with its dimensions. ' +
        'A full-size export is often too large to return; pass maxDimension (for example 512) to get a scaled ' +
        'copy back. Read-only: exporting does not change the document.',
      readOnly: true,
      inputSchema: objectSchema({
        format: { type: 'string', enum: ['png', 'jpeg', 'webp'], default: 'png', description: 'Output image format.' },
        quality: { type: 'number', minimum: 0.1, maximum: 1, default: 0.92, description: 'Encoder quality for jpeg and webp. Ignored for png.' },
        maxDimension: { type: 'integer', minimum: 16, maximum: 8192, description: 'Scale the export so its longest side is at most this many pixels.' },
        maxBytes: { type: 'integer', minimum: 1000, maximum: 5000000, default: 750000, description: 'Refuse to return a data URL larger than this many bytes.' }
      }),
      handler: (args) => commands.exportImage(args)
    }
  ]
}
